import { motion } from "framer-motion";
import { Info } from "lucide-react";

export const GlassCard = ({ children, className = "", ...rest }) => (
  <div className={`cc-card rounded-2xl ${className}`} {...rest}>
    {children}
  </div>
);

export const PageHeader = ({ label, title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
    <div>
      {label && (
        <span className="text-xs tracking-[0.2em] uppercase text-slate-500">{label}</span>
      )}
      <h1 className="cc-heading text-3xl sm:text-4xl font-600 tracking-tight text-white mt-1">
        {title}
      </h1>
      {subtitle && <p className="text-slate-400 mt-2 max-w-2xl">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const ProgressBar = ({ value, className = "" }) => (
  <div className={`h-2 w-full rounded-full bg-white/5 overflow-hidden ${className}`}>
    <motion.div
      className="h-full cc-gradient rounded-full"
      initial={{ width: 0 }}
      animate={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    />
  </div>
);

export const ScoreRing = ({ value = 0, size = 132, stroke = 10, label, sublabel }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="ccring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.07)" strokeWidth={stroke} fill="none" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} stroke="url(#ccring)" strokeWidth={stroke}
          fill="none" strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: off }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="cc-heading text-3xl font-700 text-white">{Math.round(value)}</span>
        {label && <span className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</span>}
      </div>
      {sublabel && <span className="sr-only">{sublabel}</span>}
    </div>
  );
};

export const Disclaimer = ({ text }) => (
  <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-200/80">
    <Info size={16} className="mt-0.5 shrink-0 text-amber-300" />
    <span>{text}</span>
  </div>
);

export const Empty = ({ icon: Icon, title, subtitle, children }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 px-6">
    {Icon && (
      <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl cc-surface-2 text-blue-300">
        <Icon size={26} strokeWidth={1.6} />
      </div>
    )}
    <h3 className="cc-heading text-lg font-500 text-white">{title}</h3>
    {subtitle && <p className="text-slate-400 mt-1.5 max-w-sm text-sm">{subtitle}</p>}
    {children && <div className="mt-6">{children}</div>}
  </div>
);

export const Spinner = ({ className = "" }) => (
  <div className={`flex items-center justify-center py-12 text-slate-400 ${className}`}>
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/15 border-t-blue-400" />
  </div>
);

export const Chip = ({ children, tone = "default" }) => {
  const tones = {
    default: "border-white/10 bg-white/[0.03] text-slate-300",
    blue: "border-blue-400/30 bg-blue-500/10 text-blue-200",
    purple: "border-purple-400/30 bg-purple-500/10 text-purple-200",
    green: "border-emerald-400/30 bg-emerald-500/10 text-emerald-200",
    amber: "border-amber-400/30 bg-amber-500/10 text-amber-200",
    red: "border-red-400/30 bg-red-500/10 text-red-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${tones[tone] || tones.default}`}>
      {children}
    </span>
  );
};
